/**
 * Service Worker Registration Utility
 * Регистрация и управление Service Worker для PWA функциональности
 */

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

/**
 * Регистрация Service Worker
 */
export async function registerServiceWorker(config: ServiceWorkerConfig = {}): Promise<ServiceWorkerRegistration | null> {
  // Service Worker поддерживается только в production и в HTTPS
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service Worker not supported');
    return null;
  }

  // В development режиме не регистрируем SW (мешает HMR)
  if (import.meta.env.DEV) {
    console.log('[SW] Skipping Service Worker registration in development');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none', // Всегда проверять обновления SW
    });

    console.log('[SW] Service Worker registered:', registration.scope);

    // Проверяем состояние регистрации
    if (registration.installing) {
      console.log('[SW] Service Worker installing...');
      trackInstallation(registration.installing, config);
    } else if (registration.waiting) {
      console.log('[SW] Service Worker waiting to activate');
      config.onUpdate?.(registration);
    } else if (registration.active) {
      console.log('[SW] Service Worker active');
      config.onSuccess?.(registration);
    }

    // Слушаем обновления
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        console.log('[SW] New Service Worker found');
        trackInstallation(newWorker, config);
      }
    });

    // Периодически проверяем обновления (каждый час)
    setInterval(() => {
      registration.update().catch(() => {
        // Игнорируем ошибки обновления
      });
    }, 60 * 60 * 1000);

    return registration;
  } catch (error) {
    console.error('[SW] Service Worker registration failed:', error);
    config.onError?.(error as Error);
    return null;
  }
}

/**
 * Отслеживание установки Service Worker
 */
function trackInstallation(worker: ServiceWorker, config: ServiceWorkerConfig): void {
  worker.addEventListener('statechange', () => {
    console.log('[SW] Service Worker state:', worker.state);

    if (worker.state === 'installed') {
      if (navigator.serviceWorker.controller) {
        // Новая версия готова, но старая ещё активна
        console.log('[SW] New content available, will activate on next reload');
        config.onUpdate?.(worker as unknown as ServiceWorkerRegistration);
      } else {
        // Первая установка
        console.log('[SW] Content cached for offline use');
        config.onSuccess?.(worker as unknown as ServiceWorkerRegistration);
      }
    }
  });
}

/**
 * Отмена регистрации Service Worker
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const success = await registration.unregister();
    
    if (success) {
      console.log('[SW] Service Worker unregistered');
    }
    
    return success;
  } catch (error) {
    console.error('[SW] Service Worker unregistration failed:', error);
    return false;
  }
}

/**
 * Очистка кэша Service Worker
 */
export async function clearServiceWorkerCache(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  registration.active?.postMessage('clearCache');
  
  console.log('[SW] Cache clear requested');
}

/**
 * Принудительное обновление Service Worker
 */
export async function updateServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  
  // Если есть ожидающий воркер - активируем его
  if (registration.waiting) {
    registration.waiting.postMessage('skipWaiting');
  }
  
  // Проверяем обновления
  await registration.update();
  
  console.log('[SW] Service Worker update requested');
}

/**
 * Проверка поддержки Service Worker
 */
export function isServiceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator;
}

/**
 * Получение текущего состояния Service Worker
 */
export async function getServiceWorkerStatus(): Promise<{
  supported: boolean;
  registered: boolean;
  active: boolean;
  waiting: boolean;
}> {
  if (!('serviceWorker' in navigator)) {
    return {
      supported: false,
      registered: false,
      active: false,
      waiting: false,
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    
    return {
      supported: true,
      registered: !!registration,
      active: !!registration?.active,
      waiting: !!registration?.waiting,
    };
  } catch {
    return {
      supported: true,
      registered: false,
      active: false,
      waiting: false,
    };
  }
}

